import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  asList,
  createAssignment,
  deleteAssignment,
  listAssignments,
  listBanks,
  listCourses,
  listUnits,
  updateAssignment,
} from "./api";
import {
  ActionMenu,
  ConfirmDialog,
  CounterPill,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
} from "./components/Ui";
import { ASSIGNMENT_MODES, ASSIGNMENT_MODE_LABELS } from "./constants";
import { PERMISSIONS, can } from "./permissions";
import { useApiCall } from "./useApiCall";
import "./questionData.css";

const num = (v) => Number(v || 0);
const idOf = (v) => String(v ?? "");

const EMPTY_FORM = {
  assignment_id: "",
  course_id: "",
  question_bank_id: "",
  assignment_mode: ASSIGNMENT_MODES.FULL_BANK,
  unit_ids: [],
};

const CourseAssignmentsPage = () => {
  document.title = "Course Associations | Medicotoon";
  const navigate = useNavigate();
  const call = useApiCall();

  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [courseFilter, setCourseFilter] = useState("all");
  const [bankFilter, setBankFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [modal, setModal] = useState(null); // { mode }
  const [form, setForm] = useState(EMPTY_FORM);
  const [formUnits, setFormUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  /* ---------------------------------------------------------------- */
  /* Loading                                                           */
  /* ---------------------------------------------------------------- */
  const fetchAssignments = useCallback(async () => {
    const { ok, data } = await call(() => listAssignments({}), { silent: true });
    if (ok) setAssignments(asList(data).rows);
    return ok;
  }, [call]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const [coursesRes, banksRes] = await Promise.all([
      call(() => listCourses(), { silent: true }),
      call(() => listBanks({}), { silent: true }),
    ]);

    if (coursesRes.ok) setCourses(asList(coursesRes.data).rows);
    if (banksRes.ok) setBanks(asList(banksRes.data).rows);

    const ok = await fetchAssignments();
    if (!ok) setLoadError("Failed to load course associations");
    setLoading(false);
  }, [call, fetchAssignments]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ---------------------------------------------------------------- */
  /* Lookups                                                           */
  /* ---------------------------------------------------------------- */
  const courseName = useCallback(
    (courseId) =>
      courses.find((c) => idOf(c.course_id) === idOf(courseId))?.course_name ||
      courses.find((c) => idOf(c.course_id) === idOf(courseId))?.name ||
      `Course #${courseId}`,
    [courses]
  );

  const bankName = useCallback(
    (bankId) =>
      banks.find((b) => idOf(b.question_bank_id) === idOf(bankId))?.name ||
      `Bank #${bankId}`,
    [banks]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return assignments.filter((a) => {
      const matchesCourse =
        courseFilter === "all" || idOf(a.course_id) === idOf(courseFilter);
      const matchesBank =
        bankFilter === "all" || idOf(a.question_bank_id) === idOf(bankFilter);
      const matchesTerm =
        !term ||
        courseName(a.course_id).toLowerCase().includes(term) ||
        bankName(a.question_bank_id).toLowerCase().includes(term);
      return matchesCourse && matchesBank && matchesTerm;
    });
  }, [assignments, courseFilter, bankFilter, search, courseName, bankName]);

  /* ---------------------------------------------------------------- */
  /* Form                                                              */
  /* ---------------------------------------------------------------- */
  const loadFormUnits = useCallback(
    async (bankId) => {
      if (!bankId) {
        setFormUnits([]);
        return;
      }
      setUnitsLoading(true);
      const { ok, data } = await call(() => listUnits(bankId), {
        error: "Failed to load units",
      });
      setFormUnits(ok ? asList(data).rows : []);
      setUnitsLoading(false);
    },
    [call]
  );

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormUnits([]);
    setModal({ mode: "add" });
  };

  const openEdit = async (assignment) => {
    setForm({
      assignment_id: assignment.assignment_id,
      course_id: idOf(assignment.course_id),
      question_bank_id: idOf(assignment.question_bank_id),
      assignment_mode:
        assignment.assignment_mode === ASSIGNMENT_MODES.SELECTED_UNITS
          ? ASSIGNMENT_MODES.SELECTED_UNITS
          : ASSIGNMENT_MODES.FULL_BANK,
      unit_ids: (assignment.units || []).map((u) => idOf(u.unit_id ?? u)),
    });
    setModal({ mode: "edit" });
    loadFormUnits(assignment.question_bank_id);
  };

  const onBankChange = (bankId) => {
    // Units belong to the chosen bank, so any previous selection is void.
    setForm((f) => ({ ...f, question_bank_id: bankId, unit_ids: [] }));
    loadFormUnits(bankId);
  };

  const toggleUnit = (unitId) =>
    setForm((f) => ({
      ...f,
      unit_ids: f.unit_ids.includes(idOf(unitId))
        ? f.unit_ids.filter((id) => id !== idOf(unitId))
        : [...f.unit_ids, idOf(unitId)],
    }));

  /**
   * Blocks a duplicate course-bank association before it reaches the server.
   * The backend must enforce the same rule with a unique constraint.
   */
  const duplicateExists = useMemo(() => {
    if (!form.course_id || !form.question_bank_id) return false;
    return assignments.some(
      (a) =>
        idOf(a.course_id) === idOf(form.course_id) &&
        idOf(a.question_bank_id) === idOf(form.question_bank_id) &&
        idOf(a.assignment_id) !== idOf(form.assignment_id)
    );
  }, [assignments, form]);

  const submit = async () => {
    if (!form.course_id) {
      toast.error("Select a course");
      return;
    }
    if (!form.question_bank_id) {
      toast.error("Select a question bank");
      return;
    }
    if (duplicateExists) {
      toast.error("This question bank is already attached to that course");
      return;
    }
    if (
      form.assignment_mode === ASSIGNMENT_MODES.SELECTED_UNITS &&
      !form.unit_ids.length
    ) {
      toast.error("Select at least one unit");
      return;
    }

    setSaving(true);
    // Everything is stored by ID, so later renames never break the link.
    const payload = {
      course_id: form.course_id,
      question_bank_id: form.question_bank_id,
      assignment_mode: form.assignment_mode,
      unit_ids:
        form.assignment_mode === ASSIGNMENT_MODES.SELECTED_UNITS
          ? form.unit_ids
          : [],
    };

    const { ok } =
      modal.mode === "add"
        ? await call(() => createAssignment(payload), {
            success: "Question bank attached to the course",
          })
        : await call(
            () => updateAssignment({ ...payload, assignment_id: form.assignment_id }),
            { success: "Association updated" }
          );
    setSaving(false);

    if (ok) {
      setModal(null);
      fetchAssignments();
    }
  };

  const confirmDelete = async () => {
    setSaving(true);
    const { ok } = await call(
      () => deleteAssignment(deleteTarget.assignment_id),
      { success: "Association removed" }
    );
    setSaving(false);
    if (ok) {
      setDeleteTarget(null);
      fetchAssignments();
    }
  };

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */
  if (!can(PERMISSIONS.BANK_VIEW)) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Question Data" breadcrumbItem="Course Associations" />
          <EmptyState
            icon="bx bx-lock-alt"
            title="Access denied"
            description="You do not have permission to view question bank associations."
          />
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Question Data" breadcrumbItem="Course Associations" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="qd-toolbar">
                    <div>
                      <Button
                        color="light"
                        size="sm"
                        className="mb-2"
                        onClick={() => navigate("/question-data/banks")}
                      >
                        <i className="bx bx-arrow-back me-1" />
                        Question banks
                      </Button>
                      <h4 className="mb-1">Course Associations</h4>
                      <p className="text-muted mb-0">
                        Attach a complete question bank to a course, or only the
                        units you choose. Associations are stored by ID, so
                        renaming a bank, unit or lecture never breaks them.
                      </p>
                    </div>
                    <div className="qd-toolbar-actions">
                      {can(PERMISSIONS.ASSIGN) ? (
                        <Button color="primary" onClick={openCreate}>
                          <i className="bx bx-link me-1" />
                          Attach Question Bank
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  <Row className="g-2 mt-3">
                    <Col md={4}>
                      <Input
                        type="text"
                        placeholder="Search by course or bank..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </Col>
                    <Col md={4}>
                      <Input
                        type="select"
                        value={courseFilter}
                        onChange={(e) => setCourseFilter(e.target.value)}
                      >
                        <option value="all">All courses</option>
                        {courses.map((c) => (
                          <option key={c.course_id} value={c.course_id}>
                            {c.course_name || c.name}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={4}>
                      <Input
                        type="select"
                        value={bankFilter}
                        onChange={(e) => setBankFilter(e.target.value)}
                      >
                        <option value="all">All question banks</option>
                        {banks.map((b) => (
                          <option key={b.question_bank_id} value={b.question_bank_id}>
                            {b.name}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>

                  <div className="mt-3">
                    {loading ? (
                      <LoadingState label="Loading associations..." />
                    ) : loadError ? (
                      <ErrorState message={loadError} onRetry={fetchAll} />
                    ) : !assignments.length ? (
                      <EmptyState
                        icon="bx bx-link"
                        title="No associations yet"
                        description="Attach a question bank to a course to give it access to those questions."
                        action={
                          can(PERMISSIONS.ASSIGN) ? (
                            <Button color="primary" onClick={openCreate}>
                              <i className="bx bx-link me-1" />
                              Attach Question Bank
                            </Button>
                          ) : null
                        }
                      />
                    ) : !filtered.length ? (
                      <EmptyState
                        icon="bx bx-search"
                        title="No matching associations"
                        description="Try a different search term or clear the filters."
                      />
                    ) : (
                      <div className="table-responsive">
                        <table className="table align-middle qd-table">
                          <thead>
                            <tr>
                              <th>Course</th>
                              <th>Question Bank</th>
                              <th>Mode</th>
                              <th>Scope</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.map((a) => {
                              const selectedUnits =
                                a.assignment_mode === ASSIGNMENT_MODES.SELECTED_UNITS
                                  ? a.units || []
                                  : [];
                              return (
                                <tr key={a.assignment_id}>
                                  <td>
                                    <div className="fw-semibold">
                                      {courseName(a.course_id)}
                                    </div>
                                    <div className="text-muted small">
                                      course_id: {a.course_id}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fw-semibold">
                                      {bankName(a.question_bank_id)}
                                    </div>
                                    <div className="text-muted small">
                                      question_bank_id: {a.question_bank_id}
                                    </div>
                                  </td>
                                  <td>
                                    <span
                                      className={`badge ${
                                        a.assignment_mode ===
                                        ASSIGNMENT_MODES.FULL_BANK
                                          ? "bg-primary"
                                          : "bg-info"
                                      }`}
                                    >
                                      {ASSIGNMENT_MODE_LABELS[a.assignment_mode] ||
                                        a.assignment_mode}
                                    </span>
                                  </td>
                                  <td>
                                    {a.assignment_mode ===
                                    ASSIGNMENT_MODES.FULL_BANK ? (
                                      <span className="text-muted">
                                        All units and lectures in this bank
                                      </span>
                                    ) : selectedUnits.length ? (
                                      <div className="qd-unit-chips">
                                        {selectedUnits.map((u) => (
                                          <span
                                            className="badge bg-light text-dark"
                                            key={u.unit_id ?? u}
                                          >
                                            {u.name || `Unit #${u.unit_id ?? u}`}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-muted">No units</span>
                                    )}
                                    {num(a.questions_count) ? (
                                      <div className="mt-1">
                                        <CounterPill
                                          count={num(a.questions_count)}
                                          label="questions available"
                                        />
                                      </div>
                                    ) : null}
                                  </td>
                                  <td className="text-end">
                                    <ActionMenu
                                      actions={[
                                        {
                                          key: "open",
                                          label: "Open question bank",
                                          icon: "bx bx-show",
                                          onClick: () =>
                                            navigate(
                                              `/question-data/banks/${a.question_bank_id}`
                                            ),
                                        },
                                        {
                                          key: "edit",
                                          label: "Edit association",
                                          icon: "bx bx-edit",
                                          hidden: !can(PERMISSIONS.ASSIGN),
                                          onClick: () => openEdit(a),
                                        },
                                        { key: "d1", divider: true },
                                        {
                                          key: "remove",
                                          label: "Remove association",
                                          icon: "bx bx-unlink",
                                          danger: true,
                                          hidden: !can(PERMISSIONS.ASSIGN_REMOVE),
                                          onClick: () => setDeleteTarget(a),
                                        },
                                      ]}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Create / edit association */}
      <Modal isOpen={!!modal} toggle={() => !saving && setModal(null)} centered size="lg">
        <ModalHeader toggle={() => !saving && setModal(null)}>
          {modal?.mode === "add" ? "Attach Question Bank" : "Edit Association"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <Field label="Course" required>
                <Input
                  type="select"
                  value={form.course_id}
                  disabled={modal?.mode === "edit"}
                  onChange={(e) => setForm({ ...form, course_id: e.target.value })}
                >
                  <option value="">Select a course...</option>
                  {courses.map((c) => (
                    <option key={c.course_id} value={c.course_id}>
                      {c.course_name || c.name}
                    </option>
                  ))}
                </Input>
              </Field>
            </Col>
            <Col md={6}>
              <Field label="Question bank" required>
                <Input
                  type="select"
                  value={form.question_bank_id}
                  disabled={modal?.mode === "edit"}
                  onChange={(e) => onBankChange(e.target.value)}
                >
                  <option value="">Select a question bank...</option>
                  {banks.map((b) => (
                    <option key={b.question_bank_id} value={b.question_bank_id}>
                      {b.name}
                    </option>
                  ))}
                </Input>
              </Field>
            </Col>
          </Row>

          {duplicateExists ? (
            <div className="alert alert-warning py-2 small">
              <i className="bx bx-error me-1" />
              This question bank is already attached to that course. Edit the
              existing association instead of creating a duplicate.
            </div>
          ) : null}

          <Field label="Association mode" required>
            <div className="qd-mode-choices">
              <label
                className={`qd-mode-choice ${
                  form.assignment_mode === ASSIGNMENT_MODES.FULL_BANK ? "is-active" : ""
                }`}
              >
                <input
                  type="radio"
                  name="assignment_mode"
                  checked={form.assignment_mode === ASSIGNMENT_MODES.FULL_BANK}
                  onChange={() =>
                    setForm({
                      ...form,
                      assignment_mode: ASSIGNMENT_MODES.FULL_BANK,
                      unit_ids: [],
                    })
                  }
                />
                <div>
                  <div className="fw-semibold">Attach Entire Question Bank</div>
                  <div className="text-muted small">
                    The course gets every unit, lecture and question in the bank,
                    including anything added later.
                  </div>
                </div>
              </label>

              <label
                className={`qd-mode-choice ${
                  form.assignment_mode === ASSIGNMENT_MODES.SELECTED_UNITS
                    ? "is-active"
                    : ""
                }`}
              >
                <input
                  type="radio"
                  name="assignment_mode"
                  checked={form.assignment_mode === ASSIGNMENT_MODES.SELECTED_UNITS}
                  onChange={() =>
                    setForm({
                      ...form,
                      assignment_mode: ASSIGNMENT_MODES.SELECTED_UNITS,
                    })
                  }
                />
                <div>
                  <div className="fw-semibold">Attach Selected Units</div>
                  <div className="text-muted small">
                    Only questions inside the units you tick are available to the
                    course.
                  </div>
                </div>
              </label>
            </div>
          </Field>

          {form.assignment_mode === ASSIGNMENT_MODES.SELECTED_UNITS ? (
            <Field label="Units" required>
              {!form.question_bank_id ? (
                <div className="text-muted">Select a question bank first.</div>
              ) : unitsLoading ? (
                <div className="text-muted">
                  <Spinner size="sm" className="me-2" />
                  Loading units...
                </div>
              ) : !formUnits.length ? (
                <div className="text-muted">This bank has no units yet.</div>
              ) : (
                <div className="qd-unit-picker">
                  {formUnits.map((u) => (
                    <label className="qd-unit-option" key={u.unit_id}>
                      <input
                        type="checkbox"
                        checked={form.unit_ids.includes(idOf(u.unit_id))}
                        onChange={() => toggleUnit(u.unit_id)}
                      />
                      <span className="flex-grow-1">{u.name}</span>
                      <CounterPill
                        count={num(u.questions_count)}
                        label="questions"
                      />
                    </label>
                  ))}
                </div>
              )}
            </Field>
          ) : null}

          <div className="alert alert-info py-2 small mb-0 mt-2">
            <i className="bx bx-info-circle me-1" />
            {form.assignment_mode === ASSIGNMENT_MODES.FULL_BANK
              ? "Every unit in this bank will be created as a unit inside the course. Units added to the bank later are added to the course automatically."
              : "Each selected unit will be created as a unit inside the course."}{" "}
            Existing course units with the same name are reused, not duplicated.
          </div>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button color="light" onClick={() => setModal(null)} disabled={saving}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={submit}
              disabled={saving || duplicateExists}
            >
              {saving ? <Spinner size="sm" className="me-1" /> : null}
              {modal?.mode === "add" ? "Attach" : "Save changes"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Remove association"
        message={
          deleteTarget
            ? `Remove "${bankName(deleteTarget.question_bank_id)}" from "${courseName(
                deleteTarget.course_id
              )}"?`
            : ""
        }
        warning="The question bank itself is not deleted - only this course loses access to it."
        confirmLabel="Remove"
        busy={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ToastContainer />
    </React.Fragment>
  );
};

export default CourseAssignmentsPage;
